// Utility functions for growth chart calculations and coordinate mapping

/**
 * Calculate age in months from two dates
 */
export const calculateAgeInMonths = (birthDate, measurementDate) => {
  // Parse DD/MM/YYYY format
  const parseBirthDate = (dateStr) => {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Parse YYYY-MM-DD format
  const parseMeasurementDate = (dateStr) => {
    return new Date(dateStr);
  };

  const birth = parseBirthDate(birthDate);
  const measurement = parseMeasurementDate(measurementDate);

  const years = measurement.getFullYear() - birth.getFullYear();
  const months = measurement.getMonth() - birth.getMonth();
  const days = measurement.getDate() - birth.getDate();

  let totalMonths = years * 12 + months;
  if (days < 0) {
    totalMonths -= 1;
  }

  return totalMonths;
};

/**
 * Calculate BMI (Body Mass Index)
 */
export const calculateBMI = (weight, height) => {
  if (!weight || !height) return null;
  // weight in kg, height in cm
  const heightInMeters = height / 100;
  return (weight / (heightInMeters * heightInMeters)).toFixed(2);
};

/**
 * Map age and measurement values to pixel coordinates on growth charts
 * These mappings are approximations based on WHO growth chart scales
 * Charts are rotated 90 degrees, so we need to account for that
 */

// Chart 1: Weight for Age (Birth to 5 years)
// Age: 0-60 months, Weight: 0-25 kg (approximate)
export const mapWeightForAge = (ageMonths, weight, chartWidth = 250, chartHeight = 350) => {
  if (ageMonths > 60) return null; // Outside chart range
  
  // Simple mapping for testing
  // X-axis (age): 0-60 months mapped across width
  // Y-axis (weight): 0-25 kg mapped across height
  const x = (ageMonths / 60) * chartWidth;
  const y = chartHeight - ((weight / 25) * chartHeight);
  
  console.log(`Weight for Age: age=${ageMonths}mo, weight=${weight}kg -> x=${x}, y=${y}`);
  
  return { x, y };
};

// Chart 2: Length/Height for Age (Birth to 5 years)
// Age: 0-60 months, Height: 45-120 cm (approximate)
export const mapHeightForAge = (ageMonths, height, chartWidth = 250, chartHeight = 350) => {
  if (ageMonths > 60) return null;
  
  // X-axis (age): 0-60 months
  // Y-axis (height): 45-120 cm
  const x = (ageMonths / 60) * chartWidth;
  const y = chartHeight - (((height - 45) / (120 - 45)) * chartHeight);
  
  console.log(`Height for Age: age=${ageMonths}mo, height=${height}cm -> x=${x}, y=${y}`);
  
  return { x, y };
};

// Chart 3: Weight for Length/Height (Birth to 5 years)
// Height: 45-120 cm, Weight: 0-25 kg (approximate)
export const mapWeightForHeight = (height, weight, chartWidth = 250, chartHeight = 350) => {
  if (height < 45 || height > 120) {
    console.log(`Weight for Height out of range: height=${height}cm (need 45-120)`);
    return null;
  }
  
  // X-axis (height): 45-120 cm
  // Y-axis (weight): 0-25 kg
  const x = ((height - 45) / (120 - 45)) * chartWidth;
  const y = chartHeight - ((weight / 25) * chartHeight);
  
  console.log(`Weight for Height: height=${height}cm, weight=${weight}kg -> x=${x}, y=${y}`);
  
  return { x, y };
};

// Chart 4: Head Circumference for Age (Birth to 5 years)
// Age: 0-60 months, Head: 30-55 cm (approximate)
export const mapHeadCircumferenceForAge = (ageMonths, headCircumference, chartWidth = 250, chartHeight = 350) => {
  if (ageMonths > 60 || !headCircumference) {
    console.log(`Head Circumference out of range: age=${ageMonths}mo (need 0-60), head=${headCircumference}cm`);
    return null;
  }
  
  // X-axis (age): 0-60 months
  // Y-axis (head circumference): 30-55 cm
  const x = (ageMonths / 60) * chartWidth;
  const y = chartHeight - (((headCircumference - 30) / (55 - 30)) * chartHeight);
  
  console.log(`Head Circumference: age=${ageMonths}mo, head=${headCircumference}cm -> x=${x}, y=${y}`);
  
  return { x, y };
};

// Chart 5: Weight and Height (0-18 years) - Not rotated
// Age: 0-216 months (0-18 years), dual axes chart
// We'll plot based on age for x-axis, can show both weight and height points
export const mapWeightAndHeightChart5 = (ageMonths, weight, height, chartWidth = 250, chartHeight = 350) => {
  if (ageMonths > 216) {
    console.log(`Chart 5 out of range: age=${ageMonths}mo (need 0-216)`);
    return null;
  }
  
  // X-axis (age): 0-216 months
  // For this chart, we'll plot weight on the lower portion and height on upper portion
  // This is an approximation - the actual chart has dual y-axes
  const x = (ageMonths / 216) * chartWidth;
  
  // Weight range approximately 0-80 kg, Height range approximately 45-180 cm
  // We'll create two separate points if both exist
  const points = [];
  
  if (weight) {
    const y = chartHeight - ((weight / 80) * (chartHeight / 2)); // Lower half for weight
    points.push({ x, y, type: 'weight' });
    console.log(`Chart 5 Weight: age=${ageMonths}mo, weight=${weight}kg -> x=${x}, y=${y}`);
  }
  
  if (height) {
    const y = (chartHeight / 2) - (((height - 45) / (180 - 45)) * (chartHeight / 2)); // Upper half for height
    points.push({ x, y, type: 'height' });
    console.log(`Chart 5 Height: age=${ageMonths}mo, height=${height}cm -> x=${x}, y=${y}`);
  }
  
  return points.length > 0 ? points : null;
};

// Chart 6: BMI for Age (5-18 years)
// Age: 60-216 months (5-18 years), BMI: 10-35 (approximate)
export const mapBMIForAge = (ageMonths, bmi, chartWidth = 250, chartHeight = 350) => {
  if (ageMonths < 60 || ageMonths > 216 || !bmi) {
    console.log(`BMI out of range: age=${ageMonths}mo (need 60-216), bmi=${bmi}`);
    return null;
  }
  
  // X-axis (age): 60-216 months (5-18 years)
  // Y-axis (BMI): 10-35
  const x = ((ageMonths - 60) / (216 - 60)) * chartWidth;
  const y = chartHeight - (((bmi - 10) / (35 - 10)) * chartHeight);
  
  console.log(`BMI for Age: age=${ageMonths}mo, bmi=${bmi} -> x=${x}, y=${y}`);
  
  return { x, y };
};

/**
 * Get all chart points for a baby's growth records
 */
export const getChartPoints = (growthRecords, birthDate) => {
  const points = {
    weightForAge: [],      // Chart 1: Birth to 5 years (0-60 months)
    heightForAge: [],      // Chart 2: Birth to 5 years (0-60 months)
    weightForHeight: [],   // Chart 3: Birth to 5 years
    headCircumference: [], // Chart 4: Birth to 5 years (0-60 months)
    weightAndHeight: [],   // Chart 5: 0 to 18 years (0-216 months)
    bmi: []               // Chart 6: 5 to 18 years (60-216 months)
  };

  console.log('Processing growth records:', growthRecords.length);

  growthRecords.forEach(record => {
    const ageMonths = calculateAgeInMonths(birthDate, record.date_recorded || record.date);
    console.log(`Record: age=${ageMonths} months, weight=${record.weight}kg, height=${record.height}cm, head=${record.head_circumference}cm`);
    
    // Charts 1-4: Only for babies 0-60 months (Birth to 5 years)
    if (ageMonths <= 60) {
      // Chart 1: Weight for Age (0-60 months)
      if (record.weight) {
        const point = mapWeightForAge(ageMonths, record.weight);
        if (point) {
          points.weightForAge.push({ ...point, record, ageMonths });
        }
      }
      
      // Chart 2: Height for Age (0-60 months)
      if (record.height) {
        const point = mapHeightForAge(ageMonths, record.height);
        if (point) {
          points.heightForAge.push({ ...point, record, ageMonths });
        }
      }
      
      // Chart 3: Weight for Height
      if (record.weight && record.height) {
        const point = mapWeightForHeight(record.height, record.weight);
        if (point) {
          points.weightForHeight.push({ ...point, record, ageMonths });
        }
      }
      
      // Chart 4: Head Circumference (0-60 months)
      if (record.head_circumference) {
        const point = mapHeadCircumferenceForAge(ageMonths, record.head_circumference);
        if (point) {
          points.headCircumference.push({ ...point, record, ageMonths });
        }
      }
    }
    
    // Chart 5: Weight and Height (0-216 months = 0-18 years)
    if (ageMonths <= 216 && (record.weight || record.height)) {
      const chartPoints = mapWeightAndHeightChart5(ageMonths, record.weight, record.height);
      if (chartPoints) {
        chartPoints.forEach(point => {
          points.weightAndHeight.push({ ...point, record, ageMonths });
        });
      }
    }
    
    // Chart 6: BMI for Age (60-216 months = 5-18 years)
    if (record.weight && record.height && ageMonths >= 60 && ageMonths <= 216) {
      const bmi = calculateBMI(record.weight, record.height);
      const point = mapBMIForAge(ageMonths, parseFloat(bmi));
      if (point) {
        points.bmi.push({ ...point, record, bmi, ageMonths });
      }
    }
  });

  console.log('Chart points calculated:', {
    weightForAge: points.weightForAge.length,
    heightForAge: points.heightForAge.length,
    weightForHeight: points.weightForHeight.length,
    headCircumference: points.headCircumference.length,
    weightAndHeight: points.weightAndHeight.length,
    bmi: points.bmi.length
  });

  return points;
};
